import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useT } from "@/i18n/helpers";
import { FadeUp } from "@/components/Reveal";
import Seo from "@/components/Seo";

/* ------------------------------------------------------------------ */
/*  Produits & Terroirs. Fleur de sel & Miel. Présentation épurée.   */
/*  Aucun prix, aucune certification, aucun volume inventé.           */
/* ------------------------------------------------------------------ */

const Product = ({ p, index }) => {
  const even = index % 2 === 0;
  return (
    <section data-testid={`terroir-${p.id}`} className="border-t border-nanda-line">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <FadeUp className={even ? "md:order-1" : "md:order-2"}>
          <div className="overflow-hidden rounded-3xl">
            <img src={p.img} alt={p.alt} loading="lazy" className="w-full aspect-[4/3] object-cover" />
          </div>
        </FadeUp>
        <FadeUp delay={0.12} className={even ? "md:order-2" : "md:order-1"}>
          <div className="label-overline text-nanda-terracotta mb-6">{p.badge}</div>
          <h2 className="font-sans font-light tracking-tight text-nanda-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-balance">
            {p.title}
          </h2>
          <p className="mt-7 max-w-lg text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed">{p.desc}</p>
          <ul className="mt-8 space-y-3">
            {p.points.map((pt, i) => (
              <li key={i} className="flex items-baseline gap-3 text-sm text-nanda-ink" data-testid={`terroir-${p.id}-point-${i}`}>
                <span className="h-px w-5 bg-nanda-terracotta shrink-0 translate-y-[-3px]" />
                {pt}
              </li>
            ))}
          </ul>
        </FadeUp>
      </div>
    </section>
  );
};

export default function ProduitsTerroirs() {
  const tt = useT();

  const PRODUCTS = [
    {
      id: "fleur-de-sel",
      badge: tt("Fleur de sel", "Fleur de sel", "Flor de sal"),
      title: tt("Des cristaux délicats, récoltés au soleil.", "Delicate crystals, harvested under the sun.", "Cristais delicados, colhidos ao sol."),
      desc: tt(
        "Une fleur de sel artisanale du Sénégal, prélevée à la surface des salines et séchée au soleil. Un geste patient, un produit simple.",
        "An artisanal fleur de sel from Senegal, skimmed from the surface of the salt pans and sun-dried. A patient gesture, a simple product.",
        "Uma flor de sal artesanal do Senegal, recolhida à superfície das salinas e seca ao sol. Um gesto paciente, um produto simples."
      ),
      points: [
        tt("Récolte artisanale, à la main", "Artisanal hand harvest", "Colheita artesanal, à mão"),
        tt("Séchage solaire naturel", "Natural sun drying", "Secagem solar natural"),
        tt("Origine Sénégal", "Senegal origin", "Origem Senegal"),
      ],
      img: "/photos/nanda-sa-food/fleur-de-sel.jpg",
      alt: tt("Fleur de sel artisanale du Sénégal", "Artisanal fleur de sel from Senegal", "Flor de sal artesanal do Senegal"),
    },
    {
      id: "miel",
      badge: tt("Miel", "Honey", "Mel"),
      title: tt("Un miel sauvage, entre mangrove et forêt.", "A wild honey, between mangrove and forest.", "Um mel selvagem, entre mangal e floresta."),
      desc: tt(
        "Un miel récolté au Sénégal, entre mangrove et forêt. Filtré avec soin, sans transformation inutile, juste le goût du terroir.",
        "A honey harvested in Senegal, between mangrove and forest. Carefully filtered, without unnecessary processing, just the taste of the terroir.",
        "Um mel colhido no Senegal, entre o mangal e a floresta. Filtrado com cuidado, sem transformação desnecessária, apenas o sabor do terroir."
      ),
      points: [
        tt("Récolte auprès d'apiculteurs partenaires", "Harvested with partner beekeepers", "Colheita com apicultores parceiros"),
        tt("Filtration douce, sans additifs", "Gentle filtration, no additives", "Filtração suave, sem aditivos"),
        tt("Traçabilité par récolte", "Traceability by harvest", "Rastreabilidade por colheita"),
      ],
      img: "/photos/nanda-sa-food/miel.jpg",
      alt: tt("Miel du Sénégal", "Senegal honey", "Mel do Senegal"),
    },
  ];

  return (
    <div data-testid="page-terroirs" className="bg-white">
      <Seo path="/terroirs" />

      {/* HERO */}
      <section className="border-b border-nanda-line">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 pt-36 md:pt-48 pb-20 md:pb-28">
          <FadeUp>
            <div className="label-overline text-nanda-muted mb-8">
              {tt("Produits & Terroirs", "Products & Terroirs", "Produtos & Terroirs")}
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-sans font-light tracking-tight text-nanda-ink text-5xl sm:text-6xl md:text-7xl leading-[1.02] text-balance" data-testid="terroirs-hero-title">
              {tt("Simplement précieux.", "Simply precious.", "Simplesmente precioso.")}
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-10 max-w-2xl text-nanda-inkSoft text-lg md:text-xl font-light leading-relaxed">
              {tt(
                "Deux produits du terroir du Sénégal, choisis avec soin : la fleur de sel et le miel. D'autres rejoindront cette sélection, au rythme de nos exigences.",
                "Two terroir products from Senegal, carefully chosen: fleur de sel and honey. Others will join this selection, at the pace of our standards.",
                "Dois produtos do terroir do Senegal, escolhidos com cuidado: a flor de sal e o mel. Outros juntar-se-ão a esta selecção, ao ritmo das nossas exigências."
              )}
            </p>
          </FadeUp>
        </div>
      </section>

      {/* PRODUITS */}
      {PRODUCTS.map((p, i) => (
        <Product key={p.id} p={p} index={i} />
      ))}

      {/* CTA */}
      <section className="border-t border-nanda-line bg-nanda-bgAlt">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <FadeUp className="md:col-span-8">
            <h2 className="font-sans font-light tracking-tight text-nanda-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-balance">
              {tt(
                "Une question sur nos produits du terroir ?",
                "A question about our terroir products?",
                "Uma questão sobre os nossos produtos do terroir?"
              )}
            </h2>
            <p className="mt-6 max-w-xl text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed">
              {tt(
                "Particuliers, restaurateurs, distributeurs : écrivez-nous, nous répondons personnellement.",
                "Individuals, restaurateurs, distributors: write to us, we answer personally.",
                "Particulares, restauradores, distribuidores: escrevam-nos, respondemos pessoalmente."
              )}
            </p>
          </FadeUp>
          <FadeUp delay={0.15} className="md:col-span-4 flex md:justify-end">
            <Link
              to="/contact?subject=Terroirs"
              data-testid="terroirs-cta-contact"
              className="inline-flex items-center gap-2.5 bg-nanda-terracotta text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300"
            >
              {tt("Nous écrire", "Write to us", "Escreva-nos")}
              <ArrowUpRight size={15} strokeWidth={1.5} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
